import { ActionIcon, Badge } from '@mantine/core';
import { Pencil, TriangleAlert } from 'lucide-react';

import { type ChainSlotStatus, formatGapWarningText } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { cn } from '@/utils/styleUtils.ts';

const STATUS_STYLE: Record<
    ChainSlotStatus['kind'],
    { row: string; circle: string; badge?: { label: string; color: string } }
> = {
    available: {
        row: 'bg-green-50',
        circle: 'border-green-500 text-green-600',
    },
    'out-of-gap': {
        row: 'bg-yellow-50',
        circle: 'border-yellow-500 text-yellow-600 bg-yellow-50',
        badge: { label: 'Abstand nicht beachtet', color: 'yellow' },
    },
    'not-available': {
        row: 'bg-red-50',
        circle: 'border-slate-300 text-slate-500',
        badge: { label: 'Konflikt', color: 'red' },
    },
};

interface Props {
    position: number;
    stepOrder: number;
    appointmentTypeName: string;
    practitionerName: string;
    startDatetime: string | undefined;
    durationMinutes: number;
    status: ChainSlotStatus;
    onEdit?: (order: number) => void;
}

export const ChainStepCard = ({
    position,
    stepOrder,
    appointmentTypeName,
    practitionerName,
    startDatetime,
    durationMinutes,
    status,
    onEdit,
}: Props) => {
    const style = STATUS_STYLE[status.kind];
    const start = startDatetime ? dayjs(startDatetime) : undefined;
    const handleEditClick = (): void => onEdit?.(stepOrder);

    return (
        <div className={cn('flex w-full items-center justify-between px-4 py-3', style.row)}>
            <div className="flex flex-nowrap items-center gap-4">
                <div
                    className={cn(
                        'flex h-7 shrink-0 items-center justify-center rounded-full border-2 px-3 text-sm font-medium',
                        style.circle
                    )}
                >
                    {position}
                </div>
                <div className="flex min-w-64 flex-col">
                    <span className="font-medium">
                        {start ? start.format('dddd, D. MMMM YYYY') : 'Kein Slot verfügbar'}
                    </span>
                    <span className="text-sm text-slate-600">
                        {start && `${start.format('HH:mm')} · ${durationMinutes} min · `}
                        <span>{appointmentTypeName}</span>
                        {' · '}
                        <span>{practitionerName}</span>
                    </span>
                    {status.kind === 'out-of-gap' && (
                        <span className="mt-0.5 flex items-center gap-1 text-xs text-black">
                            <TriangleAlert className="size-3 shrink-0" />
                            {formatGapWarningText(status)}
                        </span>
                    )}
                </div>
                {style.badge && (
                    <Badge
                        color={style.badge.color}
                        variant="filled"
                        leftSection={<TriangleAlert className="size-3" />}
                        title={status.kind === 'out-of-gap' ? formatGapWarningText(status) : undefined}
                    >
                        {style.badge.label}
                    </Badge>
                )}
            </div>
            {onEdit && (
                <ActionIcon variant="subtle" color="gray" onClick={handleEditClick} aria-label="Termin bearbeiten">
                    <Pencil className="size-4" />
                </ActionIcon>
            )}
        </div>
    );
};
